import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { ChangeEvent, Fragment, memo, useCallback, useMemo } from 'react';

import { Button, Checkbox, IconSvg, Text } from '@/lib/v2/components';
import { SizeButton } from '@/lib/v2/components/Button/Button';
import { DropdownItemProps } from '@/lib/v2/components/Dropdown';

type Items = DropdownItemProps & { checked?: boolean };
type DataItems = { label: string; items: Items[] };

export type CheckboxItems = Record<string, DataItems>;

type DropdownFilterSectionProps = {
  id?: string | number;
  isFullWidth?: boolean;
  tooltip?: string;
  className?: string;
  isDisabled?: boolean;
  icon?: JSX.Element;
  isOnlyIcon?: boolean;
  color?: 'standard' | 'primary';
  size?: SizeButton;
  label: string;
  isMenuOnTheLeft?: boolean;
  dropdownItems: CheckboxItems;
  onClickApplyFilter?: (checkedItems: CheckboxItems) => void;
  isScrollable?: boolean;
  applyButtonText?: string;
  onChangeCheckbox?: (isChecked: boolean, section: string, index: number) => void;
};

const DropdownFilterSection = ({
  id,
  isFullWidth = false,
  tooltip,
  className,
  isDisabled,
  icon,
  isOnlyIcon,
  color = 'standard',
  size,
  label,
  isMenuOnTheLeft,
  dropdownItems,
  onClickApplyFilter,
  isScrollable = false,
  applyButtonText = 'Apply',
  onChangeCheckbox,
}: DropdownFilterSectionProps) => {
  const classesMenuItems = classNames(
    'absolute z-20 mt-2 w-60 origin-top-right rounded-md bg-white shadow-md ring-1 ring-black/5 focus:outline-none p-4',
    {
      'right-0': isMenuOnTheLeft,
    }
  );

  const itemsContainerClass = classNames(
    isScrollable ? 'visible-scrollbar overflow-y-scroll overflow-x-hidden max-h-[250px] mb-2' : ''
  );

  const iconRightComponent = <IconSvg svgComponent={<ChevronDownIcon aria-hidden="true" />} />;

  const totalCheckedItems = Object.values(dropdownItems).reduce((total, current) => {
    current.items.forEach((dropDownItem) => {
      if (dropDownItem.checked) {
        total += 1;
      }
    });
    return total;
  }, 0);

  const itemsMemo = useMemo(() => {
    return dropdownItems;
  }, [dropdownItems]);

  const handleOnClickButton = useCallback(() => {
    const checkedItems = Object.entries(itemsMemo).reduce(
      (acc: CheckboxItems, [section, items]) => {
        acc[section] = {
          label: items.label,
          items: items.items.filter((item) => item.checked),
        };
        return acc;
      },
      {}
    );
    onClickApplyFilter?.(checkedItems);
  }, [itemsMemo, onClickApplyFilter]);

  const handleOnChangeCheckbox = useCallback(
    (e: ChangeEvent<HTMLInputElement>, section: string, index: number) => {
      const isChecked = e.target.checked;
      onChangeCheckbox?.(isChecked, section, index);
    },
    [onChangeCheckbox]
  );

  return (
    <Menu
      as="div"
      className={classNames('relative inline-block text-left', { 'w-full': isFullWidth })}
      data-testid="dropdown-component"
      id={String(id)}
    >
      <div>
        <Menu.Button as="div" title={tooltip}>
          <Button
            className={className}
            disabled={isDisabled}
            iconLeft={icon}
            iconRight={iconRightComponent}
            id={`${id}-dropdown-button`}
            onlyIcon={isOnlyIcon}
            outline={color === 'standard'}
            primary={color === 'primary'}
            secondary={color === 'standard'}
            size={size}
            standard={color === 'standard'}
          >
            <>
              {label}
              {totalCheckedItems !== 0 && <strong>({totalCheckedItems})</strong>}
            </>
          </Button>
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items static className={classesMenuItems}>
          <div className={itemsContainerClass}>
            {Object.entries(itemsMemo).map(([section, dropdownItem]) => {
              if (dropdownItem.items.length === 0) {
                return null;
              }

              const sectionLabel = dropdownItem.label;

              const checkboxItemsElement = dropdownItem.items.map((item, index) => {
                const { id, label, value, checked } = item;

                const isChecked = checked ?? false;

                return (
                  <div key={id} className="mb-0.5">
                    <Checkbox
                      checked={isChecked}
                      id={id.toString()}
                      label={label}
                      value={value}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleOnChangeCheckbox(e, section, index)
                      }
                    />
                  </div>
                );
              });

              return (
                <div key={section} className="mb-4 flex w-full flex-col">
                  <Text className="mb-0.5" fontWeight="medium">
                    {sectionLabel}
                  </Text>
                  {checkboxItemsElement}
                </div>
              );
            })}
          </div>
          {applyButtonText && (
            <Menu.Item>
              {({ close }) => (
                <div className="flex w-full justify-end">
                  <Button
                    id={`${String(id)}-button`}
                    onClick={() => (handleOnClickButton(), close())}
                  >
                    {applyButtonText}
                  </Button>
                </div>
              )}
            </Menu.Item>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default memo(DropdownFilterSection);
